<template>
    <div class="c-appointment-edit" ref="container">
        <BackButton routeName="appointment-list" />

        <div class="center-content" v-if="appointment != null">
            <ClientNamesTabs :appointment="appointment" v-model:clientindex="currentClientIndex"
                v-if="!isNewAppointment && appointment !== null && !isNewClient && step != STEP_FINAL"/>

            <div v-if="step == STEP_BED_SIDE" class="step-container">
                <ClientSingleSelect
                    property="bedside"
                    :options="['left','right']"
                    v-model:client="currentClient"
                    @onChange="changeBedSide"
                />
                <div class="select-error" v-if="selectError">
                    {{$t('appointment_edit.error_bedside')}}
                </div>
                <ClientNavButtons
                    nextStep="STEP_GENDER"
                    @prevStep="prevStep"
                    @showStep="showStep"
                />
            </div>

            <div v-if="step == STEP_GENDER" class="step-container">
                <ClientSingleSelect
                    property="gender"
                    :options="['m','f','x']"
                    v-model:client="currentClient"
                />
                 <div class="select-error" v-if="selectError">
                    {{$t('appointment_edit.error_gender')}}
                </div>
                <ClientNavButtons
                    prevStep="STEP_BED_SIDE"
                    nextStep="STEP_SIZE"
                    @prevStep="prevStep"
                    @showStep="showStep"
                />
            </div>

            <div v-if="step == STEP_SIZE" class="step-container">
                <h1>{{$t('appointment_edit.size')}}</h1>
                <ClientSizeInput
                    v-model:size="currentClient.additional_info.size"
                />
                <div class="select-error" v-if="selectError">
                    {{$t('appointment_edit.error_size')}}
                </div>
                <ClientNavButtons
                    prevStep="STEP_GENDER"
                    nextStep="STEP_SLEEP_POSITION"
                    @prevStep="prevStep"
                    @showStep="showStep"
                />
            </div>

            <div v-if="step == STEP_SLEEP_POSITION" class="step-container">
                <ClientSingleSelect
                    property="sleep_position"
                    :options="['back','side','stomach']"
                    v-model:client="currentClient"
                />
                <div class="select-error" v-if="selectError">
                    {{$t('appointment_edit.error_position')}}
                </div>
                <ClientNavButtons
                    prevStep="STEP_SIZE"
                    nextStep="STEP_BASE"
                    @prevStep="prevStep"
                    @showStep="showStep"
                />
            </div>

             <div v-if="step == STEP_BASE" class="step-container">
                <ClientSingleSelect
                    property="base"
                    :options="['boxspring','base','other']"
                    v-model:client="currentClient"
                />
                <div class="select-error" v-if="selectError">
                    {{$t('appointment_edit.error_base')}}
                </div>
                <ClientNavButtons
                    prevStep="STEP_SLEEP_POSITION"
                    nextStep="STEP_EXTRAS"
                    @prevStep="prevStep"
                    @showStep="showStep"
                />
            </div>

             <div v-if="step == STEP_EXTRAS" class="step-container">
                <ClientMultiSelect
                    :options="['mites','sweat','temperature', 'tv', 'reading']"
                    v-model:client="currentClient"
                />
                <ClientNavButtons
                    prevStep="STEP_BASE"
                    nextStep="STEP_INFO"
                    @prevStep="prevStep"
                    @showStep="showStep"
                />
            </div>

            <div v-if="step == STEP_INFO" class="step-container">
                <ClientForm v-if="appointment !== null"
                    :client="currentClient"
                    :formErrors="formErrors"
                    :showCopyAddress="currentClientIndex == 1"
                    @copyaddress="duplicateAddress"
                />

                <ClientNavButtons
                    prevStep="STEP_EXTRAS"
                    nextStep="STEP_FINAL"
                    :buttonsDisabled="loadingForm"
                    @prevStep="prevStep"
                    @showStep="showStep"
                />
            </div>

            <div v-if="step == STEP_FINAL">
                <h1>{{$t('appointment_edit.last_step')}}</h1>

                <div class="next-btns">
                    <button class="btn-next" @click.prevent="addAnotherClient" :disabled="appointment.clients.length == 2">
                        <img class="btn-next--icon" src="/img/icon-new-user-white.svg" />
                        <span class="btn-next--label">
                            {{$t('appointment_edit.add_person')}}
                        </span>
                    </button>

                    <button class="btn-next" @click.prevent="scheduleAppointment">
                        <img class="btn-next--icon" src="/img/icon-calendar.svg" />
                        <span class="btn-next--label">
                            {{$t('appointment_edit.schedule', {names:clientNames})}}
                        </span>
                    </button>
                </div>
            </div>
        </div>

        <ConfirmPopup v-if="confirmRouteChange"
            :title="$t('appointment_edit.confirm_route_change')"
            color="blue"
            @cancel="cancelConfirmRouteChange"
            @confirm="clickConfirmRouteChange"
         />
    </div>
</template>

<script>
import {isValidEmail} from "@/utils/utils.js";
import BackButton from "@/components/app/BackButton.vue";
import ConfirmPopup from "@/components/app/ConfirmPopup.vue";
import ClientForm from "@/components/appointments/ClientForm.vue";
import ClientNavButtons from "@/components/appointments/ClientNavButtons.vue";
import ClientSizeInput from "@/components/appointments/ClientSizeInput.vue";
import ClientMultiSelect from "@/components/appointments/ClientMultiSelect.vue";
import ClientSingleSelect from "@/components/appointments/ClientSingleSelect.vue";
import ClientNamesTabs from "@/components/ClientNamesTabs.vue";
import {apiStore} from "@/stores/api";
import {socketStore} from "@/stores/socket";

export default {
    name : "AppointmentEdit",
    components : {
        ClientForm,
        ConfirmPopup,
        ClientNavButtons,
        ClientSingleSelect,
        ClientSizeInput,
        ClientMultiSelect,
        BackButton,
        ClientNamesTabs
    },
    data() {
        return {
            STEP_BED_SIDE : "STEP_BED_SIDE",
            STEP_GENDER : "STEP_GENDER",
            STEP_SIZE : "STEP_SIZE",
            STEP_SLEEP_POSITION : "STEP_SLEEP_POSITION",
            STEP_BASE : "STEP_BASE",
            STEP_EXTRAS : "STEP_EXTRAS",
            STEP_INFO : "STEP_INFO",
            STEP_FINAL : "STEP_FINAL",
            step : "",
            isNewAppointment : false,
            isNewClient : false,
            currentClient : null,
            currentClientIndex : 0,
            appointment : null,
            formErrors : {
                lastname : false,
                firstname : false,
                email : false,
                gdpr:false
            },
            selectError : false,
            confirmRouteChange : false,
            routeExitCallback : null,
            windowWidth: 0,
            windowHeight: 0,
            loadingForm: false
        };
    },

    async created() {
        if (this.$route.params.id) {
            this.appointment = await apiStore().getAppointment(this.$route.params.id);

            //force additional_info to object if empty
            for (let i = 0; i < this.appointment.clients.length; i++) {
                if (i, this.appointment.clients[i].additional_info.length === 0) {
                    this.appointment.clients[i].additional_info = {};
                }
            }

        } else {
            this.appointment = {clients:[{additional_info:{}}]};
            this.isNewAppointment = true;
            this.isNewClient = true;
        }
        this.currentClient = this.appointment.clients[this.currentClientIndex];

        if (this.$route.query.addClient !== undefined) {
            this.addAnotherClient();
        } else {
            this.step = this.STEP_BED_SIDE;
        }

        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
    },

    beforeRouteLeave(to, from, next) {
        if (this.isNewClient && this.step !== this.STEP_FINAL) {
            this.confirmRouteChange = true;
            this.routeExitCallback = next;

        } else {
            next();
        }
    },

    computed: {
        clientNames() {
            return this.appointment.clients.map(x => x.firstname).join(" & ");
        }
    },

    watch: {
        currentClient: {
            immediate: true,
            deep: true,
            handler() {
                if (!this.isNewClient && this.currentClient !== null) {
                    clearInterval(this.saveTimeout);
                    this.saveTimeout = setTimeout(() => {
                        this.updateClient();
                    }, 500); //prevents calling save method too much when dragging slider
                }
            }
        },

        currentClientIndex() {
            this.currentClient = this.appointment.clients[this.currentClientIndex];
        },

        "currentClient.lastname" : {
            immediate: false,
            deep: true,
            handler() {
                if (!this.currentClient.lastname) return;

                if (this.currentClient.lastname.length === 1) {
                    this.currentClient.lastname = this.currentClient.lastname.toUpperCase(); //first letter to uppercase
                }
            }
        },

        "currentClient.firstname" : {
            immediate: false,
            deep: true,
            handler() {
                if (!this.currentClient.firstname) return;

                if (this.currentClient.firstname.length === 1) {
                    this.currentClient.firstname = this.currentClient.firstname.toUpperCase(); //first letter to uppercase
                }
            }
        }
    },

    methods: {
        async createAppointmentWithClient() {
            if (this.currentClientIndex === 0) {
                //create new appointment
                this.appointment = await apiStore().createAppointmentWithClient(this.appointment.clients[0], this.$i18n.locale, false);

            } else if (this.currentClientIndex === 1) {
                //save 2nd client
                this.appointment = await apiStore().addClientToAppointment(this.currentClient, this.appointment);
            }

            socketStore().emit("APPOINTMENT_UPDATED");
        },

        async updateClient() {
            await apiStore().updateClient(this.currentClient);
        },

        async showStep(step) {
            if (this.step === this.STEP_BED_SIDE) {
                this.checkField("bedside", step);

            } else if (this.step === this.STEP_GENDER) {
                this.checkField("gender", step);

            } else if (this.step === this.STEP_SIZE) {
                this.checkField("size", step);

            } else if (this.step === this.STEP_SLEEP_POSITION) {
                this.checkField("sleep_position", step);

            } else if (this.step === this.STEP_BASE) {
                this.checkField("base", step);

            } else if (this.step === this.STEP_INFO) {
                let hasError = false;
                for (const key in this.formErrors) {
                    if (key === "gdpr") continue;

                    const val = this.currentClient[key];
                    this.formErrors[key] = val === undefined || val === "";
                    if (this.formErrors[key]) {
                        hasError = true;
                    }
                }

                if (!isValidEmail(this.currentClient.email)) {
                    hasError = true;
                    this.formErrors.email = true;
                }

                const bday = this.currentClient.additional_info.bday;
                const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
                const dateCheck = Date.parse(bday);
                if (bday !== undefined && bday !== "") {
                    if (!dateRegex.test(bday) || isNaN(dateCheck)) {
                        hasError = true;
                    }
                }


                this.formErrors.gdpr = false;
                if (!hasError && this.currentClient.additional_info.gdpr !== true) {
                    this.formErrors.gdpr = true;
                    hasError = true;
                }

                if (!hasError) {
                    if (this.isNewAppointment || this.isNewClient) {
                        this.loadingForm = true;
                        await this.createAppointmentWithClient();
                        this.loadingForm = false;

                    } else {
                        await this.updateClient();
                    }

                    if (!this.appointment.ready_for_measurement && !this.isNewAppointment) {
                        this.appointment.ready_for_measurement = true;
                        await apiStore().updateAppointment(this.appointment);
                    }

                    this.step = step;
                }
            } else {
                this.step = step;
            }
        },

        prevStep(step) {
            this.step = step;
            this.selectError = false;
        },

        checkField(field, step) {
            this.selectError = this.currentClient.additional_info[field] === undefined;
            if (!this.selectError) {
                this.step = step;
            }
        },

        addAnotherClient() {
            const othterBedside = this.currentClient.additional_info.bedside === "left" ? "right" : "left";

            this.isNewClient = true;
            this.step = this.STEP_GENDER;
            this.currentClientIndex = 1;
            this.appointment.clients.push({additional_info:{bedside:othterBedside}});
        },

        scheduleAppointment() {
            this.$router.push({name : "appointment-schedule", params:{id : this.appointment.id}});
        },

        duplicateAddress() {
            const fields = ["street", "number", "postal", "city"];
            let clients = this.appointment.clients;

            for (const idx in fields) {
                const field = fields[idx];
                if (clients[0].additional_info[field]) {
                    clients[1].additional_info[field] = clients[0].additional_info[field];
                }
            }

            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },

        changeBedSide(bedSide) {
            this.$nextTick(() => {
                this.bedsideError = false;

                //change bedside of other person
                if (this.appointment.clients.length > 1) {
                    this.appointment.clients[1 - this.currentClientIndex].additional_info.bedside = bedSide === "left" ? "right" : "left";
                    apiStore().updateAppointment(this.appointment);
                }
            });
        },

        cancelConfirmRouteChange() {
            this.confirmRouteChange = false;
        },

        clickConfirmRouteChange() {
            this.confirmRouteChange = false;
            this.routeExitCallback();
        }
    }
};
</script>

<style lang="scss" scoped>
    .c-appointment-edit {
        height : 100%;
    }

    .next-btns {
        padding-top: 6vh;
        display: flex;
        justify-content: center;

        button {
            &:nth-child(1) {
                @include widthBothIntro(650ms, 500ms, center)
            }
            &:nth-child(2) {
                @include widthBothIntro(650ms, 850ms, center)
            }
        }

        $size : 26vh;
        .btn-next {
            @extend .reset-btn;
            background-image : url('/img/confirm-btn-blue.png');
            background-size: 100%;
            background-repeat: no-repeat;
            margin: 0 2vw;
            width: $size;
            height: $size;
            position: relative;

            &--label {
                color : black;
                font-size: 1.25rem;
                width: 100%;
                position: absolute;
                left: 0;
                top: 15rem;
            }

            &--icon {
                width : 25%;
            }

            &:disabled {
                opacity: .5;
            }
        }
    }

    .select-error {
        color: $color-red;
        font-size: 1.4rem;
        margin-top: 5rem;
    }

    .option {
        text-align: left;
        padding-left: 1rem;
    }

    .step-container {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        height: 100%;
    }

</style>